import "./style.scss";

import { createFilterOptions, Tooltip } from "@mui/material";
import CurrencyIcon from "components/shared/CurrencyIcon";
import { CalculationMethod, ChannelKind, CollectiveManagementKind } from "containers/Channels";
import type { IChannel } from "containers/Channels/types";
import type { IWorkTitleSummary } from "containers/Works/types";
import type { TCountries } from "countries";
import { getCountries } from "countries";
import type { ICurrencyOption } from "currenciesList";
import { isWithinInterval } from "date-fns";
import { canAddSalesForKind } from "helpers/channelCapabilities";
import { Body, BodyHighlight, SmallBody, SmallBodyHighlight } from "helpers/TypographyComponents";
import {
    getCleanStartorEndDay,
    getCurrenciesOptions,
    getCurrencyData,
    incrementDateYears,
    translationsWithFallback,
} from "helpers/utils";
import { useCompanySettings } from "hooks/useCompanySettings";
import { useWorkTitles } from "hooks/useWorkTitles";
import { t } from "i18next";
import { useCallback, useEffect, useMemo, useState, type HTMLAttributes } from "react";
import type { SubmitHandler } from "react-hook-form";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { getCalculationBaseColumnLabels } from "store/calculationBaseColumnLabelsStore";
import { fetchTitleChannels } from "store/channelStore";
import { fetchAddSales } from "store/managementStore";
import { useAppDispatch, useAppSelector } from "store/store";
import { ChannelCalculationMethod, ChannelType } from "store/types";

import { getAddSaleDateStatus } from "./getAddSaleDateStatus";
import SelectFormComponent from "../FormComponent/SelectFormComponent";
import AddTextButton from "../AddTextButton";
import ChannelIcon from "../ChannelIcon";
import CurrencyAutocomplete from "../CurrencyAutocomplete";
import AutocompleteFormComponent from "../FormComponent/AutocompleteFormComponent";
import DateFormComponent from "../FormComponent/DateFormComponent";
import NumberFormComponent from "../FormComponent/NumberFormComponent";
import TextFormComponent from "../FormComponent/TextFormComponent";
import MessageBlock from "../MessageBlock";
import StatusChip from "../StatusChip";
import StyledButton from "../StyledButton";
import TitleCover from "../TitleCover";

type TForm = {
    titleId?: string | number;
    saleTitle: IWorkTitleSummary | null;
    channel: IChannel | null;
    channelId: number;
    date: string;
    periodId: string | number;
    countryMarket: TCountries | string | null;
    note?: string;
    returnedSales?: string | number;
    saleDate?: Date;
    termId: number;
    overridenPrice?: number | null;
    overridenPriceCurrency?: string | ICurrencyOption | null;
    distributorGrossPrice?: number | null;
    distributorInvoicingPercentage?: number | null;
    collectiveManagementKind?: CollectiveManagementKind;
    /**
     * Calculation-base columns of the line. A sale can fill all of them at once: the contract decides
     * which one its royalties are computed on. A package sale is a lump sum, always carried by the turnover.
     */
    quantity?: number | null;
    turnover?: number | null;
    turnover_1?: number | null;
    turnover_2?: number | null;
};

type TProps = {
    onClose: () => void;
    termId: number | null;
    from: "table" | "details";
    isPeriodClosed?: boolean;
    currentPeriodId?: number;
    onSalesCreated?: () => void;
};

const AddSalesModal = ({ onClose, termId, from, isPeriodClosed = false, currentPeriodId, onSalesCreated }: TProps) => {
    const [t] = useTranslation();
    const dispatch = useAppDispatch();
    const { getCompanySettings, companyInfos } = useCompanySettings();

    const term = useAppSelector((state) => state.management.generalTabData).find(
        (stateTerm) => stateTerm.id === termId,
    );
    const { activePeriod, allPeriods, activePeriodData: currentPeriod } = useAppSelector((state) => state.period);
    const { titleChannelsStatus, titleChannels: termChannels } = useAppSelector((state) => state.channels);
    const { labels: calculationBaseColumnLabels, labelsStatus: calculationBaseColumnLabelsStatus } = useAppSelector(
        (state) => state.calculationBaseColumnLabels,
    );

    /**
     * Labels of the calculation-base columns, renamed by the company when it wants to. A company only
     * fills turnover_1 / turnover_2 once it has named them, so their label doubles as the flag telling
     * whether the column is part of its sales model.
     */
    const calculationBaseLabels = useMemo(() => {
        const labelOf = (column: string) =>
            calculationBaseColumnLabels.find((label) => label.calculationBaseColumn === column)?.label;

        return {
            quantity: labelOf("quantity") || `${t("AddSales.FirstType")}`,
            turnover: labelOf("turnover") || `${t("AddSales.SecondType")}`,
            turnover_1: labelOf("turnover_1"),
            turnover_2: labelOf("turnover_2"),
        };
    }, [calculationBaseColumnLabels, t]);

    const usesMarketSegmentation = getCompanySettings("uses_market_segmentation");
    const usesOverridenPriceSales = getCompanySettings("uses_overriden_price_on_sales");
    const usesMultipleCurrencies = getCompanySettings("uses_multiple_currencies");
    const activeAlternativeCurrencies = JSON.parse(getCompanySettings("active_alternative_currencies"));
    const usesDistributorSalesData = getCompanySettings("uses_distributor_sales_data");

    const [wantsPriceOnSales, setWantsPriceOnSales] = useState<boolean>(false);
    const [submitError, setSubmitError] = useState<string | null>(null);
    const { titles: workTitles, isLoading: workTitlesLoading } = useWorkTitles(term?.workId);
    const mustChooseTitle = Boolean(term?.workId);

    const defaultCurrency = getCurrencyData(companyInfos?.currency ?? "");

    const isTodayInPeriod = useMemo(() => {
        if (!currentPeriod?.periodStart || !currentPeriod?.periodEnd) return false;
        const start = new Date(currentPeriod.periodStart);
        const end = new Date(currentPeriod.periodEnd);
        if (isNaN(start.getTime()) || isNaN(end.getTime()) || start > end) return false;
        return isWithinInterval(new Date(), { start, end });
    }, [currentPeriod?.periodStart, currentPeriod?.periodEnd]);

    const termChannelsOptions = useMemo(() => {
        return termChannels.filter((channel) => {
            if (channel.calculationMethod === ChannelCalculationMethod.SUBSCRIPTION) return false;
            if (isPeriodClosed) return canAddSalesForKind(channel.kind, true);
            return true;
        });
    }, [termChannels, isPeriodClosed]);

    const {
        control,
        setValue,
        handleSubmit,
        watch,
        formState: { errors, isSubmitting },
    } = useForm<TForm>({
        defaultValues: {
            titleId: "",
            saleTitle: null,
            channel: null,
            channelId: -1,
            note: "",
            countryMarket: null,
            returnedSales: "0",
            saleDate: isTodayInPeriod ? undefined : new Date(currentPeriod?.periodStart ?? ""),
            overridenPrice: null,
            overridenPriceCurrency: companyInfos?.currency,
            distributorGrossPrice: null,
            distributorInvoicingPercentage: null,
            quantity: null,
            turnover: null,
            turnover_1: null,
            turnover_2: null,
        },
        mode: "all",
    });

    const chosenChannel = watch("channel");
    const chosenSaleTitle = watch("saleTitle");
    const chosenDateRaw = watch("saleDate");
    const isForfait = chosenChannel?.calculationMethod === CalculationMethod.PACKAGE;

    const watchedQuantity = watch("quantity");
    const watchedTurnover = watch("turnover");
    const watchedTurnover1 = watch("turnover_1");
    const watchedTurnover2 = watch("turnover_2");

    // The rejection message names a column: it stops applying as soon as the user fills one of them.
    useEffect(() => {
        setSubmitError(null);
    }, [watchedQuantity, watchedTurnover, watchedTurnover1, watchedTurnover2]);

    /**
     * A sale must fill at least one calculation-base column. Which one its royalties are computed on
     * is decided by each contract of the title, so no single column can be required here.
     */
    const carriesACalculationBase = useMemo(() => {
        const isFilled = (value: string | number | null | undefined) =>
            value !== "" && value !== null && value !== undefined;

        return [watchedQuantity, watchedTurnover, watchedTurnover1, watchedTurnover2].some(isFilled);
    }, [watchedQuantity, watchedTurnover, watchedTurnover1, watchedTurnover2]);

    const chosenDate = useMemo(() => {
        // Force the date to the start of the day in UTC
        // in order to avoid timezone issues
        if (chosenDateRaw instanceof Date && !isNaN(chosenDateRaw.getTime())) {
            return getCleanStartorEndDay(chosenDateRaw);
        }

        return chosenDateRaw;
    }, [chosenDateRaw]);

    const currenciesOptions = useMemo(
        () => getCurrenciesOptions(activeAlternativeCurrencies),
        [activeAlternativeCurrencies],
    );

    const handleUsingPrice = () => {
        if (wantsPriceOnSales) {
            setWantsPriceOnSales(false);
        } else {
            setWantsPriceOnSales(true);
        }
    };

    /**
     * Monetary fields travel to the API in cents.
     */
    const toCentsOrNull = (value: number | string | null | undefined) => {
        if (value === null || value === undefined || value === "") return null;
        return Math.round(+value.toString().replace(",", ".") * 100);
    };

    const toNumberOrNull = (value: number | string | null | undefined) => {
        if (value === null || value === undefined || value === "") return null;
        return +value.toString().replace(",", ".");
    };

    /**
     * Turns a rejected creation into a message the user can act on. The API answers with the column
     * a contract needs, named here with the label the company gave it.
     */
    const describeSubmitError = (payload: { message?: string; data?: string } | undefined) => {
        if (payload?.message === "ServerError.MissingMilestoneCalculationBase") {
            const column = payload.data as keyof typeof calculationBaseLabels | undefined;
            const columnLabel = column && calculationBaseLabels[column];

            if (columnLabel) return t("AddSales.MissingMilestoneColumn", { column: columnLabel });
        }

        return translationsWithFallback(payload?.message ?? "", "ServerError.InternalServerError");
    };

    const onSubmit: SubmitHandler<TForm> = async (data: TForm) => {
        const selectedTitleId = isForfait ? undefined : mustChooseTitle ? data.saleTitle?.id : term?.titleId;
        if (currentPeriod && activePeriod && term && chosenChannel && (isForfait || selectedTitleId)) {
            const req = { ...data };
            req.channelId = (data.channel as IChannel).id;
            if (selectedTitleId) req.titleId = selectedTitleId;
            else delete req.titleId;
            req.periodId = currentPeriodId ?? activePeriod;
            req.termId = term.id;
            /**
             * The line only carries its calculation-base columns: the API derives from them the
             * values it stores, following the channel it belongs to.
             */
            req.quantity = toNumberOrNull(data.quantity);
            req.turnover = toCentsOrNull(data.turnover);
            req.returnedSales = data.returnedSales && Math.round(+data?.returnedSales || 0);
            // Set hours to 12 then create a new date so as not to have timestamp.
            req.date = (data.saleDate ?? new Date()).toLocaleDateString("en-CA");
            req.countryMarket = (data?.countryMarket as TCountries)?.code ?? "";
            req.overridenPrice = data.overridenPrice ? Math.round(+data.overridenPrice * 100) : null;
            req.overridenPriceCurrency = usesMultipleCurrencies
                ? (data.overridenPriceCurrency as ICurrencyOption)?.code ?? companyInfos?.currency
                : null;
            req.distributorGrossPrice = data.distributorGrossPrice
                ? Math.round(+data.distributorGrossPrice.toString().replace(",", ".") * 100)
                : null;
            req.distributorInvoicingPercentage = data.distributorInvoicingPercentage
                ? Math.round(+data.distributorInvoicingPercentage.toString().replace(",", ".") * 100)
                : null;
            req.turnover_1 = toCentsOrNull(data.turnover_1);
            req.turnover_2 = toCentsOrNull(data.turnover_2);
            const result = await dispatch(fetchAddSales(req));

            // The sale was refused: keep the modal open with the entered values so nothing is retyped.
            if (fetchAddSales.rejected.match(result)) {
                setSubmitError(describeSubmitError(result.payload as { message?: string; data?: string }));
                return;
            }

            setSubmitError(null);
            if (from === "details") {
                onSalesCreated?.();
            }
            onClose();
        }
    };

    useEffect(() => {
        if (term) {
            dispatch(fetchTitleChannels(term.id));
        }
    }, [dispatch, term, termId]);

    useEffect(() => {
        if (workTitles.length === 1) {
            setValue("saleTitle", workTitles[0]);
            return;
        }
        if (term?.legacyTermTitleId == null) return;
        const legacyTitle = workTitles.find((title) => Number(title.id) === Number(term.legacyTermTitleId));
        if (legacyTitle) setValue("saleTitle", legacyTitle);
    }, [setValue, term?.legacyTermTitleId, workTitles]);

    useEffect(() => {
        if (calculationBaseColumnLabelsStatus === "idle") {
            dispatch(getCalculationBaseColumnLabels());
        }
    }, [calculationBaseColumnLabelsStatus, dispatch]);

    useEffect(() => {
        if (isPeriodClosed && termChannels.length > 0) {
            const collectiveManagementChannel = termChannels.find((ch) => canAddSalesForKind(ch.kind, true));
            if (collectiveManagementChannel) setValue("channel", collectiveManagementChannel);
        }
    }, [termChannels, isPeriodClosed, setValue]);

    const addPriceBtn = {
        clicked: t("AddSales.PriceBtn.Clicked"),
        pending: t("AddSales.PriceBtn.Pending"),
    };

    /**
     * Formats options for Channel Autocomplete.
     */
    const renderChannelsOptions = (props, option: IChannel) => {
        return (
            <div {...props} className="sales-channels-autocomplete">
                <div
                    style={{
                        display: "flex",
                        gap: "10px",
                        alignItems: "center",
                        maxWidth: "335px",
                    }}
                >
                    <ChannelIcon channelKind={option.kind} />
                    <Tooltip title={option.name} arrow>
                        <div className="add-sales-channel-name">{option.name}</div>
                    </Tooltip>
                </div>
                <span className={`add-sales-channel-type tag${option.type ?? option.calculationMethod}`}>
                    {option.calculationMethod === ChannelCalculationMethod.PACKAGE
                        ? t("AddSales.ThirdType")
                        : option.type === ChannelType.AMOUNT_BASED
                          ? t("AddSales.SecondType")
                          : t("AddSales.FirstType")}
                </span>
            </div>
        );
    };

    /**
     * Filter function for channel autocomplete.
     */
    const filterOptions = createFilterOptions({
        matchFrom: "start",
        stringify: (option: IChannel) => option.name,
    });

    const filterTitleOptions = createFilterOptions({
        stringify: (option: IWorkTitleSummary) => `${option.name} ${option.ean13} ${option.formatTypeName ?? ""}`,
    });

    const renderTitleOptions = (props: HTMLAttributes<HTMLLIElement>, option: IWorkTitleSummary) => (
        <li {...props} className="sales-channels-autocomplete">
            <div className="add-sales-title-option">
                <TitleCover
                    title={option.name}
                    coverUrl={option.avatarUrl ?? undefined}
                    style={{ width: "28px", minWidth: "28px", fontSize: "12px" }}
                    color="title-color"
                />
                <div>
                    <div className="add-sales-channel-name">{option.name}</div>
                    <SmallBody color="grey">{option.ean13}</SmallBody>
                </div>
            </div>
            {option.formatTypeName ? (
                <StatusChip
                    status="format"
                    label={option.formatTypeName}
                    hideDot
                    tooltipText={t("Titles.FormatType")}
                    statusRules={{ format: { color: "purple", text: option.formatTypeName } }}
                />
            ) : null}
        </li>
    );

    // If they unselect a channel, clear the calculation-base columns. Mostly for visual reasons,
    // because if they unselect a channel they can't validate form.
    useEffect(() => {
        if (!chosenChannel) {
            setValue("quantity", null);
            setValue("turnover", null);
            setValue("turnover_1", null);
            setValue("turnover_2", null);
        }
        if (chosenChannel?.kind !== ChannelKind.COLLECTIVE_MANAGEMENT) {
            setValue("collectiveManagementKind", undefined);
        }
    }, [chosenChannel]);

    /**
     * Returns status of date (if different than undefined than a message must be shown: either error or warning).
     */
    const dateStatus: "error" | "warning" | "warning-otherPeriod" | "warning-otherPeriod-plural" | undefined =
        useMemo(() => {
            if (!allPeriods || !chosenDate || !currentPeriod || !chosenChannel?.kind) return;
            return getAddSaleDateStatus({
                periods: allPeriods,
                chosenDate,
                currentPeriod,
                channelKind: chosenChannel.kind,
            });
        }, [allPeriods, chosenDate, currentPeriod, chosenChannel]);

    /**
     * Returns corresponding date message based on type of error:
     */
    const dateStatusMessage = useCallback(() => {
        switch (dateStatus) {
            case "error":
                return t("AddSales.DateError");
            case "warning":
                return t("AddSales.DateWarning");
            case "warning-otherPeriod":
            case "warning-otherPeriod-plural":
                return t("AddSales.DateOtherPeriod", { count: dateStatus.includes("plural") ? 2 : 1 });
            default:
                return "";
        }
    }, [dateStatus]);

    return (
        <>
            <div className="add-sales-wrapper">
                <div className="info-wrapper">
                    <TitleCover
                        coverUrl={term?.titleAvatarUrl}
                        title={term?.title as string}
                        style={{ fontSize: "16px", width: "42px" }}
                        color={term?.workId ? "work-color" : "title-color"}
                    />
                    {term && (
                        <div className="info">
                            <Body>{term.title}</Body>
                            <SmallBodyHighlight color="grey">{term.author}</SmallBodyHighlight>
                        </div>
                    )}
                </div>
                <form onSubmit={handleSubmit(onSubmit)}>
                    <div className="channels-wraper">
                        {mustChooseTitle && !isForfait && (
                            <AutocompleteFormComponent
                                control={control}
                                controlName="saleTitle"
                                label={t("AddSales.SaleTitle")}
                                options={workTitles}
                                renderOptionText={(option: IWorkTitleSummary) =>
                                    option ? option.name : t("AddSales.ChooseATitle")
                                }
                                isOptionEqualToValue={(option: IWorkTitleSummary, value: IWorkTitleSummary) =>
                                    option.id === value.id
                                }
                                renderOption={renderTitleOptions}
                                filterOptions={filterTitleOptions}
                                loading={workTitlesLoading}
                                placeholder={t("AddSales.ChooseATitle")}
                                required
                            />
                        )}
                        <AutocompleteFormComponent
                            control={control}
                            controlName="channel"
                            label={t("AddSales.SaleChannel")}
                            helpdeskInfo={{
                                text: t("Documentation.MissingChannel"),
                                link: "https://helpdesk.crealo.app/fr/article/je-veux-ajouter-une-vente-mais-le-canal-concerne-napparait-pas-1axw3qv/",
                            }}
                            options={termChannelsOptions}
                            renderOptionText={(option: IChannel) => option.name ?? t("AddSales.ChooseAChannel")}
                            isOptionEqualToValue={(option: IChannel, value: IChannel) => option.id === value.id}
                            renderOption={renderChannelsOptions}
                            filterOptions={filterOptions}
                            loading={["idle", "pending"].includes(titleChannelsStatus)}
                            placeholder={t("AddSales.ChooseAChannel")}
                            required
                        />
                        {chosenChannel?.kind === ChannelKind.COLLECTIVE_MANAGEMENT && (
                            <SelectFormComponent
                                control={control}
                                controlName="collectiveManagementKind"
                                label={t("AddSales.CollectiveManagementKind")}
                                required
                                fullWidth
                                items={Object.values(CollectiveManagementKind).map((kind) => ({
                                    value: kind,
                                    label:
                                        kind === CollectiveManagementKind.OTHER
                                            ? t("AddSales.CollectiveManagementKindOther")
                                            : kind,
                                }))}
                            />
                        )}
                        <div
                            style={{
                                display: "flex",
                                gap: "10px",
                                flexDirection: isForfait ? "column" : "row",
                            }}
                        >
                            {isForfait ? (
                                <div
                                    style={{
                                        display: "flex",
                                        gap: "2px",
                                        flex: 1,
                                        flexDirection: "column",
                                    }}
                                >
                                    <NumberFormComponent
                                        control={control}
                                        controlName="turnover"
                                        required
                                        placeholder="0"
                                        error={errors.turnover}
                                        disabled={!chosenChannel}
                                        acceptsNegative
                                        float
                                        label={t("AddSales.ThirdType")}
                                        disabledText={t("AddSales.AmountDisabled")}
                                        icon={<CurrencyIcon sx={{ fontSize: "16px" }} />}
                                    />
                                </div>
                            ) : (
                                <>
                                    <div
                                        style={{
                                            display: "flex",
                                            gap: "2px",
                                            flex: 1,
                                            flexDirection: "column",
                                        }}
                                    >
                                        <NumberFormComponent
                                            control={control}
                                            controlName="quantity"
                                            placeholder="0"
                                            error={errors.quantity}
                                            disabled={!chosenChannel}
                                            acceptsNegative
                                            label={calculationBaseLabels.quantity}
                                            disabledText={t("AddSales.AmountDisabled")}
                                        />
                                        {!isForfait && (
                                            <SmallBody color="grey">{t("AddSales.NetQuantityInfo")}</SmallBody>
                                        )}
                                    </div>
                                    <div
                                        style={{
                                            display: "flex",
                                            gap: "2px",
                                            flex: 1,
                                            flexDirection: "column",
                                        }}
                                    >
                                        <NumberFormComponent
                                            control={control}
                                            controlName="turnover"
                                            placeholder="0"
                                            error={errors.turnover}
                                            disabled={!chosenChannel}
                                            acceptsNegative
                                            float
                                            label={calculationBaseLabels.turnover}
                                            disabledText={t("AddSales.AmountDisabled")}
                                            icon={<CurrencyIcon sx={{ fontSize: "16px" }} />}
                                        />
                                    </div>
                                </>
                            )}
                            {/* Returns are a count of units: they go with the quantity field. */}
                            {!isForfait && (
                                <div
                                    style={{
                                        display: "flex",
                                        gap: "2px",
                                        flex: 1,
                                        flexDirection: "column",
                                    }}
                                >
                                    <NumberFormComponent
                                        control={control}
                                        controlName="returnedSales"
                                        label={t("AddSales.ReturnedSales")}
                                        placeholder="0"
                                        error={errors.returnedSales}
                                        acceptsNegative
                                    />
                                </div>
                            )}
                        </div>

                        {!!chosenChannel &&
                            !isForfait &&
                            (!!calculationBaseLabels.turnover_1 || !!calculationBaseLabels.turnover_2) && (
                                <div style={{ display: "flex", gap: "10px" }}>
                                    {!!calculationBaseLabels.turnover_1 && (
                                        <div style={{ display: "flex", gap: "2px", flex: 1, flexDirection: "column" }}>
                                            <NumberFormComponent
                                                control={control}
                                                controlName="turnover_1"
                                                label={calculationBaseLabels.turnover_1}
                                                placeholder="0"
                                                error={errors.turnover_1}
                                                acceptsNegative
                                                float
                                                icon={<CurrencyIcon sx={{ fontSize: "16px" }} />}
                                            />
                                        </div>
                                    )}
                                    {!!calculationBaseLabels.turnover_2 && (
                                        <div style={{ display: "flex", gap: "2px", flex: 1, flexDirection: "column" }}>
                                            <NumberFormComponent
                                                control={control}
                                                controlName="turnover_2"
                                                label={calculationBaseLabels.turnover_2}
                                                placeholder="0"
                                                error={errors.turnover_2}
                                                acceptsNegative
                                                float
                                                icon={<CurrencyIcon sx={{ fontSize: "16px" }} />}
                                            />
                                        </div>
                                    )}
                                </div>
                            )}

                        {usesOverridenPriceSales &&
                            !!chosenChannel &&
                            chosenChannel.calculationMethod === ChannelCalculationMethod.PROPORTIONAL && (
                                <div className="overriden-price-container">
                                    <AddTextButton
                                        haveClickedOnButton={wantsPriceOnSales}
                                        action={handleUsingPrice}
                                        btnText={addPriceBtn}
                                    />
                                    {wantsPriceOnSales && (
                                        <div className="overriden-price-fields">
                                            <div style={{ flex: "1 1" }}>
                                                <NumberFormComponent
                                                    control={control}
                                                    controlName="overridenPrice"
                                                    label={t("AddSales.SpecificPrice")}
                                                    placeholder={t("AddSales.InsertPrice")}
                                                    error={errors.overridenPrice}
                                                    float
                                                />
                                            </div>
                                            {usesMultipleCurrencies && (
                                                <CurrencyAutocomplete
                                                    currenciesList={[defaultCurrency, ...currenciesOptions]}
                                                    control={control}
                                                    controlName="overridenPriceCurrency"
                                                    errors={errors}
                                                    companyCurrency={companyInfos?.currency ?? "EUR"}
                                                />
                                            )}
                                        </div>
                                    )}
                                </div>
                            )}
                        {usesMarketSegmentation && (
                            <div style={{ display: "flex", gap: "2px", flexDirection: "column" }}>
                                <BodyHighlight>{t("AddSales.CountryMarket")}</BodyHighlight>
                                <AutocompleteFormComponent
                                    control={control}
                                    controlName="countryMarket"
                                    options={getCountries().filter((country) => !country.silent)}
                                    placeholder={t("AddSales.EnterCountry")}
                                    renderOptionText={(option: TCountries) => (option ? option.name : "")}
                                    isOptionEqualToValue={(option, value) => option.code === value.code}
                                    renderOption={renderOptionCountries}
                                    defaultFilterOptions
                                    disabled={!chosenChannel}
                                    disabledText={t("AddSales.AmountDisabled")}
                                />
                            </div>
                        )}
                        {usesDistributorSalesData && !isForfait && (
                            <div style={{ display: "flex", gap: "0.5rem", flexDirection: "row" }}>
                                <div style={{ flex: 1 }}>
                                    <NumberFormComponent
                                        control={control}
                                        controlName="distributorInvoicingPercentage"
                                        label={t("AddSales.DistributorInvoicingPercentage")}
                                        placeholder="0"
                                        error={errors.distributorInvoicingPercentage}
                                        float
                                    />
                                </div>
                                <div style={{ flex: 1 }}>
                                    <NumberFormComponent
                                        control={control}
                                        controlName="distributorGrossPrice"
                                        label={t("AddSales.DistributorGrossPrice")}
                                        placeholder="0.00"
                                        error={errors.distributorGrossPrice}
                                        float
                                    />
                                </div>
                            </div>
                        )}
                        <div
                            style={{
                                display: "flex",
                                gap: "2px",
                                flexDirection: "column",
                            }}
                        >
                            <DateFormComponent
                                control={control}
                                controlName="saleDate"
                                label={t("Management.SaleDate")}
                                disabled={!chosenChannel}
                                maxDate={incrementDateYears({
                                    yearDistance: 1,
                                    referenceDate: new Date(currentPeriod?.periodEnd ?? ""),
                                })}
                            />
                        </div>

                        {dateStatus && (
                            <MessageBlock
                                type={
                                    dateStatus.includes("otherPeriod") ? "warning" : (dateStatus as "error" | "warning")
                                }
                                text={dateStatusMessage()}
                            />
                        )}

                        <div style={{ display: "flex", flexDirection: "column" }}>
                            <TextFormComponent
                                control={control}
                                controlName="note"
                                label={t("AddSales.Notes")}
                                helpdeskInfo={{
                                    text: t("Documentation.UnderstandNote"),
                                    link: "https://helpdesk.crealo.app/fr/article/a-quoi-sert-le-libelle-dune-vente-zinw3b/",
                                }}
                            />
                        </div>

                        {submitError && <MessageBlock type="error" text={submitError} />}

                        <div>
                            <StyledButton
                                buttonType="primary"
                                type="submit"
                                fullWidth
                                disabled={
                                    isSubmitting ||
                                    !!errors.quantity ||
                                    !!errors.turnover ||
                                    !carriesACalculationBase ||
                                    !chosenChannel ||
                                    (mustChooseTitle && !isForfait && !chosenSaleTitle) ||
                                    dateStatus === "error" ||
                                    (chosenChannel?.kind === ChannelKind.COLLECTIVE_MANAGEMENT &&
                                        !watch("collectiveManagementKind"))
                                }
                            >
                                {t("AddSales.AddSalesButton")}
                            </StyledButton>
                        </div>
                    </div>
                </form>
            </div>
        </>
    );
};

export default AddSalesModal;

export const renderOptionCountries = (props: object, option: { name: string; flag: string; code: string }) => {
    return (
        <li {...props} key={`country-market-${option.code}`} className="country-market-option-list">
            <Body>{option.name}</Body>
            <div style={{ display: "flex", gap: "0.25rem" }}>
                <SmallBody color="grey">{`${t("AddSales.CountryCode")}`}</SmallBody>
                <SmallBodyHighlight color="grey">{option.code}</SmallBodyHighlight>
            </div>
        </li>
    );
};
